import { Box, Button, Typography, Grid, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DrawerHeader } from '../../../components/Drawer/DrawerHeader';
import { useEffect, useState } from 'react';
import {
    useAsignarPrestadorMutation,
    useLazyGetLocalidadesPrestadoresQuery,
    useLazyGetPrestadoresQuery
} from '../../../services/periodicosApi';
import { enqueueSnackbar } from 'notistack';
import { useGetProvincesQuery } from '../../../services/companyApi';
import AutocompleteComponent from '../../../components/layout/Autocomplete';
import { tiposPrestadorOptions } from '../consts/periodicosTypes';

interface PeriodicosExamAndPrestadorDrawerProps {
    setOpen: (state: string) => void;
    employeeName: string;
    employeeRisk: string;
    selectedEmployee: any;
    reloadGrid: () => void;
    reconfirmation: boolean;
}

const PeriodicosExamAndPrestadorDrawer = ({
    setOpen,
    selectedEmployee,
    reloadGrid,
    reconfirmation = false
}: PeriodicosExamAndPrestadorDrawerProps) => {
    const { t } = useTranslation('periodicos');

    const [asignarPrestador, { isLoading }] = useAsignarPrestadorMutation();
    const [getPrestadores, { data: prestadores = [] }] = useLazyGetPrestadoresQuery({});

    const [selectedPrestador, setSelectedPrestador] = useState(selectedEmployee?.prestador || null);

    const handleSave = async () => {
        // const empleadoId = Number(String(selectedEmployee?.id).split('-')[0]);

        // if (!selectedPrestador?.id || !empleadoId) {
        //     enqueueSnackbar(t('enqueue.assignPrestador.missingData'), { variant: 'error' });
        //     return;
        // }

        // try {
        //     await asignarPrestador({
        //         idExamenEmpleados: [empleadoId],
        //         idPrestador: selectedPrestador?.id
        //     }).unwrap();

        //     enqueueSnackbar(t('enqueue.assignPrestador.success'), { variant: 'success' });
        //     setOpen('');
        //     reloadGrid();
        // } catch (err: any) {
        //     enqueueSnackbar(err?.data?.message || t('enqueue.assignPrestador.error'), {
        //         variant: 'error'
        //     });
        // }
    };

    const { data: provincesData } = useGetProvincesQuery(null);
    const provincesList = provincesData?.data?.provincias || [];
    const [selectedProvincia, setSelectedProvincia] = useState<{
        id: number;
        nombre: string;
    } | null>(null);
    const [fetchLocalidades, { data: localidadesList = [], isLoading: isLoadingLocalidades }] =
        useLazyGetLocalidadesPrestadoresQuery();
    const [selectedLocalidad, setSelectedLocalidad] = useState<{
        id: number;
        nombre: string;
    } | null>(null);

    const [selectedTipo, setSelectedTipo] = useState<{ id: number; nombre: string } | null>(null);

    useEffect(() => {
        getPrestadores({});
    }, []);

    useEffect(() => {
        const filters: any = {};

        if (selectedProvincia) filters.idProvincia = selectedProvincia.id;
        if (selectedLocalidad) filters.idLocalidad = selectedLocalidad.id;
        if (selectedTipo) filters.tipo = selectedTipo.id;

        getPrestadores(filters);
    }, [selectedProvincia, selectedLocalidad, selectedTipo]);

    useEffect(() => {
        if (selectedProvincia?.id) {
            fetchLocalidades({ idProvincia: selectedProvincia.id });
        } else {
            fetchLocalidades({});
        }

        setSelectedLocalidad(null);
    }, [selectedProvincia]);

    const handleCloseDrawer = () => {
        setOpen('');
    };

    useEffect(() => {
        if (!selectedProvincia?.id && !selectedLocalidad?.id && !selectedTipo?.id) return;
        if (selectedPrestador) {
            setSelectedPrestador(null);
        }
    }, [selectedProvincia, selectedLocalidad, selectedTipo]);

    const hanldeSelectPrestador = (selectedPrestador: any) => {
        setSelectedPrestador(selectedPrestador);
    };

    return (
        <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
            <Grid container flexDirection="column">
                <DrawerHeader
                    onIconClick={handleCloseDrawer}
                    text={`${
                        selectedEmployee?.empleado?.nombre || selectedEmployee?.nombre || ''
                    } - ${selectedEmployee?.riesgo?.codigo || ''}  ${
                        selectedEmployee?.riesgo?.descripcion || ''
                    }`}
                />

                <Typography sx={{ fontWeight: 'bold', mb: reconfirmation ? 0 : 2, mt: 4 }}>
                    {t('drawer.exam.subtitle')}
                </Typography>

                {reconfirmation && (
                    <Typography
                        sx={{
                            fontSize: '1rem',
                            font: 'Roboto',
                            fontWeight: '400'
                        }}
                    >
                        {selectedEmployee?.examenReconfirmacion || '-'}{' '}
                    </Typography>
                )}

                {!reconfirmation && (
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: '8px',
                            backgroundColor: '#E0EBED'
                        }}
                    >
                        <Typography variant="body1" fontWeight="bold">
                            {selectedEmployee?.examen?.descripcion || '-'}
                        </Typography>
                        <Typography variant="body2" mt={1}>
                            <Box component="span" fontWeight="bold">
                                {t('drawer.exam.frecuency')}
                            </Box>{' '}
                            {selectedEmployee?.examen?.frecuencia || '-'}
                        </Typography>

                        <Typography variant="body2" mt={1}>
                            <Box component="span" fontWeight="bold">
                                {t('drawer.exam.quantity')}
                            </Box>{' '}
                            {selectedEmployee?.examen?.cantidad || '-'}
                        </Typography>
                    </Box>
                )}

                <Typography
                    sx={{
                        fontWeight: 'bold',
                        mb: reconfirmation ? 0 : 2,
                        mt: reconfirmation ? 2 : 4
                    }}
                >
                    {t('drawer.prestador.subtitle')}
                </Typography>

                <Grid container spacing={2} mt={1}>
                    <Grid item xs={12}>
                        <Grid item xs={12}>
                            <AutocompleteComponent
                                label="Provincia"
                                name="provincia"
                                options={provincesList}
                                value={selectedProvincia}
                                onChange={setSelectedProvincia}
                                loading={isLoading}
                                getOptionLabel={(o: any) => o?.nombre || ''}
                                clearable
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <AutocompleteComponent
                                label="Localidad"
                                name="localidad"
                                options={localidadesList}
                                value={selectedLocalidad}
                                onChange={setSelectedLocalidad}
                                getOptionLabel={(o: any) => o?.nombre || ''}
                                loading={isLoadingLocalidades}
                                clearable
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <AutocompleteComponent
                                label="Tipo"
                                name="tipo"
                                options={tiposPrestadorOptions(t)}
                                value={selectedTipo}
                                onChange={setSelectedTipo}
                                getOptionLabel={(o: any) => o?.nombre || ''}
                                clearable
                            />

                            <AutocompleteComponent
                                label={t('drawer.form.label.prestador')}
                                name="prestador"
                                size="small"
                                options={prestadores || []}
                                value={selectedPrestador}
                                onChange={hanldeSelectPrestador}
                                clearable
                                loading={isLoading}
                                templateLabel={(option: any) => option?.razonSocial ?? ''}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                {selectedPrestador?.id && (
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: '8px',
                            backgroundColor: '#E0EBED'
                        }}
                    >
                        <Typography variant="body1" fontWeight="bold">
                            {selectedPrestador?.razonSocial || '-'}
                        </Typography>
                        <Typography variant="body2">
                            <Box component="span" fontWeight="bold">
                                {t('drawer.prestador.address')}
                            </Box>{' '}
                            {selectedPrestador?.direccion || '-'}
                        </Typography>
                        <Typography variant="body2">
                            <Box component="span" fontWeight="bold">
                                {t('drawer.prestador.cuit')}
                            </Box>{' '}
                            {selectedPrestador?.cuit || '-'}
                        </Typography>
                        <Typography variant="body2">
                            <Box component="span" fontWeight="bold">
                                {t('drawer.prestador.phone')}
                            </Box>{' '}
                            {selectedPrestador?.telefono || '-'}
                        </Typography>
                        <Typography variant="body2">
                            <Box component="span" fontWeight="bold">
                                {t('drawer.prestador.mail')}
                            </Box>{' '}
                            {selectedPrestador?.email || '-'}
                        </Typography>
                    </Box>
                )}
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button variant="outlined" onClick={handleCloseDrawer}>
                    {t('drawer.close')}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    sx={{ ml: 2, textTransform: 'none', minWidth: 100 }}
                    disabled={!selectedPrestador?.id || isLoading}
                >
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : t('drawer.save')}
                </Button>
            </Box>
        </Box>
    );
};

export default PeriodicosExamAndPrestadorDrawer;
